import { FormLayout, Label, Select, TextInput } from '@kcftech/design-system';
import { IFieldElement } from '@kcftech/prometheus-client';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { AssemblyParameterBoolFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterBoolFormContent';
import { AssemblyParameterDecimalFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterDecimalFormContent';
import { AssemblyParameterIntegerFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterIntegerFormContent';
import { AssemblyParameterSelectionFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterSelectionFormContent';
import { AssemblyParameterTextFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterTextFormContent';
import {
    AssemblyParameterFormData,
    AssemblyParameterFormInputProps,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { FunctionComponent, memo, useMemo } from 'react';
import { RegisterOptions, useFormState } from 'react-hook-form';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import { formattedParameterTypes, ParameterType } from 'utils/assembly_application';
import { Lookup } from 'utils/types';

const nameMaxLength = 128;
const codeMaxLength = 32;

const supportedTypes: ParameterType[] = [
    'text',
    'integer',
    'decimal',
    'bool',
    'selection',
];

const parameterTypeOptions = supportedTypes.map((type) => ({
    title: formattedParameterTypes[type],
    value: type,
}));

const getNameRules = (
    siblingParameters: IFieldElement[],
): RegisterOptions<AssemblyParameterFormData, 'name'> => ({
    validate: (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Parameter name is required';
        }

        if (siblingParameters.some((param) => param.FieldDisplayName === trimmedValue)) {
            return 'Parameter name already exists';
        }

        return undefined;
    },
    maxLength: {
        value: nameMaxLength,
        message: `Parameter name cannot exceed ${nameMaxLength} characters`,
    },
});

const getCodeRules = (
    siblingParameters: IFieldElement[],
): RegisterOptions<AssemblyParameterFormData, 'code'> => ({
    validate: (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Parameter code is required';
        }

        if (siblingParameters.some((param) => param.FieldKey === trimmedValue)) {
            return 'Parameter code already exists';
        }

        return undefined;
    },
    maxLength: {
        value: codeMaxLength,
        message: `Parameter code cannot exceed ${codeMaxLength} characters`,
    },
});

const typeRules: RegisterOptions<AssemblyParameterFormData, 'type'> = {
    required: 'Parameter type is required',
};

const parameterFormContentLookup: Lookup<
    ParameterType,
    FunctionComponent<AssemblyParameterFormInputProps>
> = {
    text: AssemblyParameterTextFormContent,
    bool: AssemblyParameterBoolFormContent,
    integer: AssemblyParameterIntegerFormContent,
    decimal: AssemblyParameterDecimalFormContent,
    selection: AssemblyParameterSelectionFormContent,
};

export type AssemblyParameterFormContentProps = AssemblyParameterFormInputProps &
    Readonly<{
        isEdit?: boolean;
        siblingParameters: IFieldElement[];
    }>;

type CodePopupProps = Readonly<{
    isEdit?: boolean;
}>;

const CodePopup = memo(({ isEdit }: CodePopupProps) => (
    <InfoPopup title="Code">
        {isEdit ? (
            <>
                Code cannot be edited. To change a code, delete the parameter and create
                it with a new code. Note that any assembly references to a deleted
                parameter will be deleted.
            </>
        ) : (
            <>Warning: Code cannot be edited after creation.</>
        )}
    </InfoPopup>
));

export function AssemblyParameterFormContent({
    form,
    isEdit,
    siblingParameters,
}: AssemblyParameterFormContentProps) {
    const {
        register,
        setValue,
        watch,
        control,
    } = form;

    const {
        errors,
    } = useFormState({
        control,
    });

    const { type, code } = watch();

    const ParameterFormContent = parameterFormContentLookup[type];
    const nameRules = useMemo(() => getNameRules(siblingParameters), [siblingParameters]);
    const codeRules = useMemo(() => getCodeRules(siblingParameters), [siblingParameters]);

    useEffectSkipFirst(() => {
        if (type === 'integer') {
            setValue('defaultValue', undefined);
            setValue('minValueInclusive', true);
            setValue('maxValueInclusive', true);
        } else if (type === 'decimal') {
            setValue('defaultValue', undefined);
            setValue('minValueInclusive', false);
            setValue('maxValueInclusive', false);
        } else if (type === 'bool') {
            setValue('defaultValue', false);
        } else {
            setValue('defaultValue', undefined);
            setValue('minValueInclusive', undefined);
            setValue('maxValueInclusive', undefined);
        }
    }, [type, setValue]);

    return (
        <FormLayout>
            <TextInput
                required
                label="Name"
                error={errors.name?.message}
                placeholder="Enter a name for the assembly application parameter"
                {...register('name', nameRules)}
            />

            {isEdit ? (
                <Label
                    label={
                        <div className="flex align-center">
                            Code
                            <CodePopup isEdit />
                        </div>
                    }
                >
                    {code}
                </Label>
            ) : (
                <ValidatableLabel
                    required
                    label={
                        <span className="flex align-center">
                            Code
                            <CodePopup />
                        </span>
                    }
                    error={errors.code?.message}
                >
                    <TextInput
                        label=""
                        placeholder="Enter a code for the assembly application parameter"
                        {...register('code', codeRules)}
                    />
                </ValidatableLabel>
            )}

            <Select
                ariaLabel="select assembly application parameter type"
                required
                label="Type"
                error={errors.type?.message}
                options={parameterTypeOptions}
                {...register('type', typeRules)}
            />
            {/* Prevent rendering when there is not a valid content found (type === undefined) */}
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {ParameterFormContent !== undefined && <ParameterFormContent form={form} />}
        </FormLayout>
    );
}
